import { Box, Button, Card, Link, Typography, useTheme } from "@mui/material";
import { useState } from "react";
//import { AgGridReact } from 'ag-grid-react';
//import { ColDef } from 'ag-grid-community';
import { useMedia } from "react-use";

import { useNavigate } from "react-router-dom";
import { useProfile } from "../api/hooks";

export const Privacy = ({ selectedBusiness }: { selectedBusiness: any }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [selected, setSelected] = useState<any | null>(null);
  const { profile } = useProfile();

  const isSuperAdmin =
    profile?.roles?.some((role) => role.type === "SUPER_ADMIN") ?? false;
  const isAdmin =
    (profile?.roles?.some((role) => role.type === "ADMIN") || isSuperAdmin) ??
    false;

  const hasAccess = isAdmin || isSuperAdmin;

  const isDarkTheme = useMedia("(prefers-color-scheme: dark)");
  const mobileFullWidth = "calc(100vw - 32px)";

  const isWide = useMedia("(min-width: 840px)");

  return (
    <>
      <Card
        className={!isWide ? "disable-card" : ""}
        sx={{
          width: "100%",
          maxWidth: "936px",
          margin: "0 auto",
          p: "40px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          my: "24px",
        }}
      >
        <Typography variant="h1" component="div" sx={{ fontSize: "2rem" }}>
          Spidipay Checkout integration guide
        </Typography>
        <Typography>
          Learn how to integrate the Spidipay Hosted Checkout into your app or
          website.
        </Typography>
        Currently accepted payment methods:
        <ul style={{ margin: 0 }}>
          <li>Visa</li>
          <li>Mastercard</li>
          <li>Orange Money - Senegal</li>
          <li>MTN Mobile Money - Rwanda</li>
          <li>Airtel Money - Africa</li>
          <li>Free Mobile Money</li>
          <li>Wave Money</li>
          <li>Maestro</li>
          <li>JBC</li>
          <li>Diners Club International</li>
          <li>American Express</li>
          <li>Discover</li>
        </ul>
        <Typography
          variant="h2"
          component="div"
          sx={{ fontSize: "1.5rem", mt: "16px" }}
        >
          Before you begin
        </Typography>
        <ol style={{ margin: 0 }}>
          {!profile && (
            <li style={{ margin: "12px 0" }}>
              Sign in to your Spidipay account. If you don{"'"}t have an
              account, sign up.
              <Button
                variant="contained"
                onClick={() => {
                  navigate("/signin");
                }}
                sx={{ display: "block" }}
              >
                Sign in
              </Button>
            </li>
          )}
          <li style={{ margin: "12px 0" }}>
            Add a business, if you haven{"'"}t already. You can navigate back to
            this documentation from the main menu. You have to be signed in to
            add a business.
            <Button
              variant="contained"
              disabled={!profile}
              onClick={() => {
                navigate("/admin/business-edit");
              }}
              sx={{ display: "block" }}
            >
              Add your Business
            </Button>
          </li>
          <li style={{ margin: "12px 0" }}>
            Create an API operator for the business (selected in the header) on
            the API operators page. You have to be signed in and have a business
            to create an API operator.
            <Button
              variant="contained"
              disabled={!profile}
              onClick={() => {
                navigate("/admin/operators");
              }}
              sx={{ display: "block" }}
            >
              Go to API operators
            </Button>
          </li>
          <li>
            Save the operator ID and secret key.
            <Box
              sx={{
                // eslint-disable-next-line sonarjs/no-duplicate-string
                backgroundColor: theme.palette.background.default,
                my: "16px",
                p: "16px 24px",
                borderRadius: "12px",
              }}
            >
              Never share the secret key with anyone and do not include it in
              client code.
            </Box>
          </li>
        </ol>
        <Typography variant="h2" sx={{ fontSize: "1.5rem", mt: "16px" }}>
          1. Initiate a checkout session
        </Typography>
        <Typography>
          To initiate a checkout session, send a request like the following to
          the Spidipay REST API:
        </Typography>
        <Typography variant="h3" sx={{ fontSize: "1.2rem", mt: "12px" }}>
          Request
        </Typography>
        <Box
          sx={{
            backgroundColor: theme.palette.background.default,
            p: "16px 24px",
            borderRadius: "12px",
          }}
        >
          <code>
            url: {'"'}https://api.spidipay.com/v1/checkout{'"'},
            <br />
            method: {'"'}POST{'"'},
            <br />
            headers:{" {"}
            <br />
            &nbsp;&nbsp;X-Operator-ID: "your-operator-id",
            <br />
            &nbsp;&nbsp;Authorization: "your-secret-key"
            <br />
            {"},"}
            <br />
            body:{" {"}
            <br />
            &nbsp;&nbsp;businessOrderId: "your-order-id",
            <br />
            &nbsp;&nbsp;currency: "RWF",
            <br />
            &nbsp;&nbsp;amount: 12000,
            <br />
            &nbsp;&nbsp;resultUrl: "your-result-url",
            <br />
            &nbsp;&nbsp;customer: {"{"}
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;email: "customer@email.com",
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;phone: "+250789123456",
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;name: "Customer Name",
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;taxId: "123456789",
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;address: {"{"}
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;country: "customer's-country",
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;state: "customer's-state",
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;city: "customer's-city",
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;streetAddress:
            "customer's-street-address",
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;postalCode:
            "customer's-postal-code"
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;{"}"}
            <br />
            &nbsp;&nbsp;{"}"},
            <br />
            &nbsp;&nbsp;items: {"["}
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;{"{"}
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;itemId: "item-id",
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;name: "item-name",
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;quantity: 1,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;unit: "pieces",
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;currency: "RWF",
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;amount: 5000
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;{"}"}
            <br />
            &nbsp;&nbsp;{"]"}
            <br />
            {"}"}
          </code>
        </Box>
        <ul>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">url</var>
            </code>
            : {'"'}https://api.spidipay.com{'"'} is the API URL and {'"/v1/'}
            checkout{'"'} is the checkout endpoint.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">X-Operator-ID</var>
            </code>
            : required. The ID of the API operator you would like to use.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">Authorization</var>
            </code>
            : required. The secret key of the API operator.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">businessOrderId</var>
            </code>
            : required. The unique ID of the order in your system.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">currency</var>
            </code>
            : required. Standard{" "}
            <Link
              href="https://en.wikipedia.org/wiki/ISO_4217"
              underline="none"
              target="_blank"
            >
              ISO 4217
            </Link>{" "}
            three-letter codes in upper case are used to specify currency.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">amount</var>
            </code>
            : required. Float. The amount to be paid.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">resultUrl</var>
            </code>
            : required. The URL where you want the user to be redirected after
            the Checkout.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">customer.email</var>
            </code>
            : the email address of the customer, where the payment receipt will
            be sent.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">customer.phone</var>
            </code>
            : the phone number of the customer in international E.164 format.
            E.g.
            {" '"}+250789123456{"'"}
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">customer.name</var>
            </code>
            : the name of the customer.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">customer.taxId</var>
            </code>
            : the tax ID of the customer.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">customer.address.country</var>
            </code>
            : the country of the customer.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">customer.address.state</var>
            </code>
            : the state of the customer.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">customer.address.city</var>
            </code>
            : the city of the customer.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">customer.address.streetAddress</var>
            </code>
            : the street address of the customer.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">customer.address.postalCode</var>
            </code>
            : the postal code of the customer.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items</var>
            </code>
            : the item objects in an array.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items[0].itemId</var>
            </code>
            : the itemID of the item.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items[0].name</var>
            </code>
            : the name of the item.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items[0].quantity</var>
            </code>
            : folat. The quantity of the item.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items[0].unit</var>
            </code>
            : the unit of the quantity. E.g. {" '"}pieces{"'"}
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items[0].currency</var>
            </code>
            : the currency of the item price. Standard{" "}
            <Link
              href="https://en.wikipedia.org/wiki/ISO_4217"
              underline="none"
              target="_blank"
            >
              ISO 4217
            </Link>{" "}
            three-letter codes in upper case are used to specify currency.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items[0].amount</var>
            </code>
            : float. The unit price amount of the item.
          </li>
        </ul>
        <Typography variant="h3" sx={{ fontSize: "1.2rem" }}>
          Sample Response
        </Typography>
        <Box
          sx={{
            backgroundColor: theme.palette.background.default,
            p: "16px 24px",
            borderRadius: "12px",
          }}
        >
          <code>
            {" {"}
            <br />
            &nbsp;&nbsp;id: {"'"}25950837-ba01-45b1-9cdf-02b84b75251b{"'"},
            <br />
            &nbsp;&nbsp;businessId: {"'"}cld3amth300733cuz0js2kv2d{"'"},
            <br />
            &nbsp;&nbsp;businessOrderId: {"'"}cldg1h3li0316m0uza2veyiom{"'"},
            <br />
            &nbsp;&nbsp;currency: {"'"}RWF{"'"},
            <br />
            &nbsp;&nbsp;amount: 12000,
            <br />
            &nbsp;&nbsp;resultUrl: {"'"}https://example.com/checkout/result{"'"}
            ,
            <br />
            &nbsp;&nbsp;status: {"'"}INITIATED{"'"},
            <br />
            &nbsp;&nbsp;createdAt: {"'"}2023-01-28T14:19:12.952Z{"'"},
            <br />
            &nbsp;&nbsp;checkoutUrl: {"'"}
            https://checkout.spidipay.com/pay/25950837-ba01-45b1-9cdf-02b84b75251b
            {"'"}
            <br />
            {"}"}
          </code>
        </Box>
        <ul>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">id</var>
            </code>
            : the ID of the checkout session.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">businessId</var>
            </code>
            : the ID of your business.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">businessOrderId</var>
            </code>
            : the ID of the order in your system.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">currency</var>
            </code>
            : standard{" "}
            <Link
              href="https://en.wikipedia.org/wiki/ISO_4217"
              underline="none"
              target="_blank"
            >
              ISO 4217
            </Link>{" "}
            three-letter code of the currency.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">amount</var>
            </code>
            : the amount to be paid.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">resultUrl</var>
            </code>
            : your result URL where you want the user to be redirected after the
            Checkout.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">status</var>
            </code>
            : the status of the checkout session. It is confirming that the
            checkout session has been initiated. Possible values are: {"'"}
            INITIATED{"'"}, {"'"}PENDING{"'"}, {"'"}SUCCESSFUL{"'"}, and {"'"}
            FAILED{"'"}.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">createdAt</var>
            </code>
            : the date and time when the checkout session was created in ISO
            8601 format. If the checkout session is not paid within 10 minutes
            from this, it will automatically fail. The checkout will redirect
            the user to the resultUrl 10 minutes and 5 seconds after the
            creation time at the latest.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">checkoutUrl</var>
            </code>
            : the URL where you have to redirect the user to complete the
            checkout.
          </li>
        </ul>
        <Typography variant="h2" sx={{ fontSize: "1.5rem" }}>
          2. Redirect the user to the checkout page
        </Typography>
        <Typography>
          If the checkout session is initiated, redirect the user to the
          checkoutUrl. When the checkout is completed, Spidipay will redirect
          the user to the URL you specified in the resultUrl at initialization.
        </Typography>
        <Typography variant="h2" sx={{ fontSize: "1.5rem", mt: "12px" }}>
          3. Get the result of the checkout on return
        </Typography>
        <Typography>
          To get the result of the checkout session on return, send a request
          like the following to the Spidipay API:
        </Typography>
        <Typography variant="h3" sx={{ fontSize: "1.2rem", mt: "12px" }}>
          Request
        </Typography>
        <Box
          sx={{
            backgroundColor: theme.palette.background.default,
            p: "16px 24px",
            borderRadius: "12px",
          }}
        >
          <code>
            url: {'"'}https://api.spidipay.com/v1/checkout/{"{"}businessOrderId
            {"}"}
            {'"'},
            <br />
            method: {'"'}GET{'"'},
            <br />
            headers:{" {"}
            <br />
            &nbsp;&nbsp;X-Operator-ID: "your-operator-id",
            <br />
            &nbsp;&nbsp;Authorization: "your-secret-key"
            <br />
            {"}"}
          </code>
        </Box>
        <ul>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">url</var>
            </code>
            : make sure to replace {'"{'}businessOrderId{'}"'} in the URL.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">businessOrderId</var>
            </code>
            : required. The unique ID of the order in your system.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">X-Operator-ID</var>
            </code>
            : required. The ID of the API operator you would like to use.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">Authorization</var>
            </code>
            : required. The secret key of the API operator.
          </li>
        </ul>
        <Typography variant="h3" sx={{ fontSize: "1.2rem" }}>
          Sample Response
        </Typography>
        <Box
          sx={{
            backgroundColor: theme.palette.background.default,

            p: "16px 24px",
            borderRadius: "12px",
          }}
        >
          <code>
            {" {"}
            <br />
            &nbsp;&nbsp;id: {"'"}25950837-ba01-45b1-9cdf-02b84b75251b{"'"},
            <br />
            &nbsp;&nbsp;businessOrderId: {"'"}cldg1h3li0316m0uza2veyiom{"'"},
            <br />
            &nbsp;&nbsp;currency: {"'"}RWF{"'"},<br />
            &nbsp;&nbsp;amount: 12000,
            <br />
            &nbsp;&nbsp;status: {"'"}SUCCESSFUL{"'"},
            <br />
            &nbsp;&nbsp;message: null,
            <br />
            &nbsp;&nbsp;createdAt: {"'"}2023-01-28T14:19:12.952Z{"'"},<br />
            &nbsp;&nbsp;updatedAt: {"'"}2023-01-28T14:19:32.249Z{"'"},
            <br />
            &nbsp;&nbsp;items: {"["}
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;{"{"}
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;itemId: 'item-id',
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;name: 'item-name',
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;quantity: 1,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;unit: 'pieces',
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;currency: 'RWF',
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;amount: 5000
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;{"}"}
            <br />
            &nbsp;&nbsp;{"]"}
            <br />
            {"}"}
          </code>
        </Box>
        <ul>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">id</var>
            </code>
            : the ID of the checkout session.
          </li>

          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">businessOrderId</var>
            </code>
            : the ID of the order in your system.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">currency</var>
            </code>
            : standard{" "}
            <Link
              href="https://en.wikipedia.org/wiki/ISO_4217"
              underline="none"
              target="_blank"
            >
              ISO 4217
            </Link>{" "}
            three-letter code of the currency.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">amount</var>
            </code>
            : the amount of the checkout session.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">status</var>
            </code>
            : the status of the checkout session. Possible values are: {"'"}
            INITIATED{"'"}, {"'"}PENDING{"'"}, {"'"}
            FAILED{"'"}, and {"'"}SUCCESSFUL{"'"}. After returning from the
            checkout page, the status will be {"'"}FAILED{"'"} or {"'"}
            SUCCESSFUL{"'"}.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">message</var>
            </code>
            : the message of the checkout session.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">createdAt</var>
            </code>
            : the date and time when the checkout session was created in ISO
            8601 format.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">updatedAt</var>
            </code>
            : the date and time when the checkout session was last updated in
            ISO 8601 format.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items</var>
            </code>
            : the item objects in an array.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items[0].itemId</var>
            </code>
            : the itemID of the item.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items[0].name</var>
            </code>
            : the name of the item.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items[0].quantity</var>
            </code>
            : folat. The quantity of the item.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items[0].unit</var>
            </code>
            : the unit of the quantity.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items[0].currency</var>
            </code>
            : the currency of the item price. Standard{" "}
            <Link
              href="https://en.wikipedia.org/wiki/ISO_4217"
              underline="none"
              target="_blank"
            >
              ISO 4217
            </Link>{" "}
            three-letter codes in upper case are used to specify currency.
          </li>
          <li style={{ margin: "12px 0" }}>
            <code translate="no" dir="ltr">
              <var translate="no">items[0].amount</var>
            </code>
            : float. The unit price amount of the item.
          </li>
        </ul>
        <Typography>
          After this you can see a complete summary of your checkout sessions on
          the dashboard and find their details in the checkouts while you are
          signed in.
        </Typography>
      </Card>
    </>
  );
};

export default Privacy;
